import { get, ref, remove, set, update } from 'firebase/database';
import { DateTime } from 'luxon';
import { database } from 'src/book/src/firebase';

export const NON_MANAGED_PAYMENT_METHODS = [
  { id: 'transfer', labelKey: 'paymentMethodTransfer' },
  { id: 'bit', labelKey: 'paymentMethodBit' },
  { id: 'cash', labelKey: 'paymentMethodCash' },
  { id: 'card', labelKey: 'paymentMethodCard' },
];

export function toMillis(value) {
  const numeric = Number(value);
  if (!numeric) return 0;
  return numeric > 1e12 ? numeric : numeric * 1000;
}

export function getEventDateKey(event) {
  if (event?.start) {
    const fromIso = DateTime.fromISO(event.start, { zone: 'Asia/Jerusalem' });
    if (fromIso.isValid) return fromIso.toFormat('yyyy-MM-dd');
  }

  if (event?.startTimestamp) {
    const fromMillis = DateTime.fromMillis(Number(event.startTimestamp), {
      zone: 'Asia/Jerusalem',
    });
    if (fromMillis.isValid) return fromMillis.toFormat('yyyy-MM-dd');
  }

  return null;
}

export function getEventEffectiveTimestamp(event) {
  if (event?.startTimestamp) {
    const timestamp = Number(event.startTimestamp);
    if (timestamp) return timestamp;
  }

  if (event?.start) {
    const fromIso = DateTime.fromISO(event.start);
    if (fromIso.isValid) return fromIso.toMillis();
  }

  return 0;
}

export function lookupNonManagedPayment(customerPayments, paymentId) {
  if (!customerPayments || paymentId == null) return null;
  return customerPayments[paymentId] || customerPayments[String(paymentId)] || null;
}

export function getAppointmentNonManagedPayment(
  event,
  nonManagedInvoices,
  { includeRefunded = false } = {},
) {
  if (!event?.uid) return null;

  const customerPayments = nonManagedInvoices?.[event.uid];
  if (!customerPayments) return null;

  const isRefunded = (payment) =>
    payment?.status === 'refunded' || Number(payment?.amount_refunded) > 0;
  const isVisible = (payment) => payment && (includeRefunded || !isRefunded(payment));

  const byId = lookupNonManagedPayment(customerPayments, event.nonManagedPaymentId);
  if (isVisible(byId)) return byId;

  if (!event.eventId) return null;

  const byEvent = Object.values(customerPayments).find(
    (payment) => String(payment.eventId) === String(event.eventId) && isVisible(payment),
  );

  return byEvent || null;
}

async function updateIfExists(path, data) {
  const dbRef = ref(database, path);
  const snapshot = await get(dbRef);
  if (!snapshot.exists()) return false;
  await update(dbRef, data);
  return true;
}

export async function updateEventNonManagedPaymentId(bizId, event, paymentId) {
  if (!bizId || !event?.uid || !event?.eventId) return;

  const payload = { nonManagedPaymentId: paymentId ?? null };
  const uid = event.uid;
  const eventId = event.eventId;

  await Promise.all([
    updateIfExists(`/events/biz/${bizId}/${uid}/${eventId}`, payload),
    updateIfExists(`/events/past/biz/${bizId}/${uid}/${eventId}`, payload),
  ]);

  const dateKey = getEventDateKey(event);
  if (!dateKey) return;

  await Promise.all(
    ['future', 'past'].map(async (partitionName) => {
      const datePath = `/events/partitionedByDate/${partitionName}/${bizId}/${dateKey}`;
      const dateSnapshot = await get(ref(database, datePath));
      if (!dateSnapshot.exists()) return;

      const dateNode = dateSnapshot.val();
      if (dateNode?.[eventId]) {
        await updateIfExists(`${datePath}/${eventId}`, payload);
        return;
      }

      await Promise.all(
        Object.entries(dateNode).map(async ([key, child]) => {
          if (child?.eventId === eventId || key === eventId) {
            await updateIfExists(`${datePath}/${key}`, payload);
          }
        }),
      );
    }),
  );
}

export async function saveNonManagedPayment({
  bizId,
  uid,
  event,
  amount,
  paymentMethod,
  existingId,
}) {
  const timestamp = existingId ? Number(existingId) : Date.now();
  const amountCents = Math.round(Number(amount) * 100);
  const invoiceRef = ref(database, `/billing/invoices/nonManaged/${bizId}/${uid}/${timestamp}`);
  const payload = {
    created: Date.now(),
    amount_paid: amountCents,
    eventId: event.eventId,
    paymentMethod,
  };

  const effectiveTimestamp = getEventEffectiveTimestamp(event);
  if (effectiveTimestamp) {
    payload.effectiveTimestamp = effectiveTimestamp;
  }

  if (existingId) {
    await update(invoiceRef, payload);
  } else {
    await set(invoiceRef, payload);
  }

  await updateEventNonManagedPaymentId(bizId, event, timestamp);
  return timestamp;
}

export async function removeNonManagedPayment({ bizId, uid, event, paymentId }) {
  if (!bizId || !uid || !paymentId) return;

  await remove(ref(database, `/billing/invoices/nonManaged/${bizId}/${uid}/${paymentId}`));
  await updateEventNonManagedPaymentId(bizId, event, null);
}

export async function refundNonManagedPayment({ bizId, uid, paymentId, amountPaid }) {
  if (!bizId || !uid || !paymentId) return;

  await update(ref(database, `/billing/invoices/nonManaged/${bizId}/${uid}/${paymentId}`), {
    amount_refunded: amountPaid,
    refundTimestamp: Date.now(),
  });
}
